import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const EMPTY_FORM = { project_id: "", plot_number: "", size_sqft: "", owner_id: "", has_cottage: false, nightly_rate: "" };
const fmtSqft = (n) => (n == null ? "—" : `${Math.round(Number(n)).toLocaleString("en-IN")} sq ft`);

export default function AdminPlotsPage() {
  const [plots, setPlots] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    const [p, pr, u] = await Promise.all([api.get("/plots"), api.get("/projects"), api.get("/users?role=customer")]);
    setPlots(p.data); setProjects(pr.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, size_sqft: Number(form.size_sqft), owner_id: form.owner_id || null };
      await api.post("/plots", payload);
      toast.success("Plot added");
      setOpen(false); setForm(EMPTY_FORM);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const assignOwner = async (id, owner_id) => {
    try {
      await api.patch(`/plots/${id}`, { owner_id: owner_id || null, status: owner_id ? "sold" : "available" });
      toast.success("Owner updated");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading text-3xl md:text-4xl">Plots</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-plot-btn"><Plus size={14} className="mr-2" />New plot</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New plot</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Project</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger data-testid="plot-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>Plot number</Label><Input required value={form.plot_number} onChange={(e) => setForm({ ...form, plot_number: e.target.value })} data-testid="plot-number-input" /></div>
              <div>
                <Label>Size (sq ft)</Label>
                <Input type="number" step="1" min="1" required placeholder="e.g. 21780"
                       value={form.size_sqft}
                       onChange={(e) => setForm({ ...form, size_sqft: e.target.value })}
                       data-testid="plot-size-input" />
                <div className="text-[11px] text-muted-foreground mt-1">Enter the surveyed plot area in square feet. 43,560 sq ft = 1 acre.</div>
              </div>
              <div>
                <Label>Owner (optional)</Label>
                <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>{users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2"><Switch checked={form.has_cottage} onCheckedChange={(v) => setForm({ ...form, has_cottage: v })} data-testid="plot-cottage-switch" /><span className="text-sm">Has cottage</span></div>
              {form.has_cottage && (
                <div>
                  <Label>Nightly rate (₹, overrides project default)</Label>
                  <Input type="number" step="50" min="0" placeholder="Leave blank to use project rate"
                         value={form.nightly_rate} onChange={(e) => setForm({ ...form, nightly_rate: e.target.value })}
                         data-testid="plot-rate-input" />
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={!form.project_id || !form.plot_number || !form.size_sqft} data-testid="plot-save-btn">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border rounded-lg bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="p-3">Plot</th><th className="p-3">Project</th><th className="p-3">Size</th><th className="p-3">Cottage</th><th className="p-3">Status</th><th className="p-3">Owner</th><th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {plots.map((p) => {
              const proj = projects.find((x) => x.id === p.project_id);
              return (
                <tr key={p.id} data-testid={`row-plot-${p.plot_number}`}>
                  <td className="p-3 font-medium">{p.plot_number}</td>
                  <td className="p-3 text-muted-foreground">{proj?.name || "—"}</td>
                  <td className="p-3">{fmtSqft(p.size_sqft)}</td>
                  <td className="p-3">{p.has_cottage ? "Yes" : "—"}</td>
                  <td className="p-3"><span className={`status-pill status-${p.status === "sold" ? "completed" : "pending"}`}>{p.status}</span></td>
                  <td className="p-3">
                    <Select value={p.owner_id || ""} onValueChange={(v) => assignOwner(p.id, v)}>
                      <SelectTrigger className="h-8 text-xs w-[220px]" data-testid={`assign-owner-${p.plot_number}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                      <SelectContent>{users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3"><Link className="text-primary text-xs" to={`/app/plots/${p.id}`}>Open</Link></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
